import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import {
  X, Upload, Download, Plus, Trash2, Edit3, Save, CheckCircle2, Star, StarOff,
  Power, PowerOff, ChevronDown, ChevronRight, FileDown, TrendingUp, TrendingDown, Minus,
} from "lucide-react";

// --- Sparkline: küçük SVG grafik, price serisini gösterir ---
function PriceSparkline({ series, currentPrice }) {
  if (!series || series.length < 2) {
    return <span className="text-[10px] text-neutral-300 font-mono-num">—</span>;
  }
  const w = 60, h = 20, pad = 2;
  const prices = series.map((p) => Number(p.price));
  const min = Math.min(...prices), max = Math.max(...prices);
  const range = max - min || 1;
  const step = (w - pad * 2) / Math.max(1, prices.length - 1);
  const points = prices.map((p, i) => {
    const x = pad + i * step;
    const y = h - pad - ((p - min) / range) * (h - pad * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  const first = prices[0], last = prices[prices.length - 1];
  const changePct = first > 0 ? ((last - first) / first) * 100 : 0;
  const up = changePct > 0.01;
  const down = changePct < -0.01;
  const color = up ? "#dc2626" : down ? "#16a34a" : "#737373";
  return (
    <span className="inline-flex items-center gap-1 align-middle">
      <svg width={w} height={h} className="overflow-visible">
        <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        {prices.map((p, i) => {
          const x = pad + i * step;
          const y = h - pad - ((p - min) / range) * (h - pad * 2);
          const isCurrent = series[i]?.is_current;
          return (
            <circle key={i} cx={x} cy={y} r={isCurrent ? 2.4 : 1.4}
                    fill={isCurrent ? color : "#fff"} stroke={color} strokeWidth="1" />
          );
        })}
      </svg>
      <span
        title={`${series.length} nokta · ${first.toLocaleString("tr-TR", { maximumFractionDigits: 2 })} → ${last.toLocaleString("tr-TR", { maximumFractionDigits: 2 })}`}
        className={`text-[10px] font-mono-num font-semibold ${up ? "text-rose-600" : down ? "text-emerald-700" : "text-neutral-500"}`}
      >
        {up && <TrendingUp size={9} className="inline -mt-0.5" />}
        {down && <TrendingDown size={9} className="inline -mt-0.5" />}
        {!up && !down && <Minus size={9} className="inline -mt-0.5" />}
        {" "}{up ? "+" : ""}{changePct.toLocaleString("tr-TR", { maximumFractionDigits: 1 })}%
      </span>
    </span>
  );
}
const UNITS = ["m2", "kg", "ton"];

const parseTRAmount = (v) => {
  if (v === null || v === undefined || v === "") return NaN;
  let s = String(v).trim();
  if (s.includes(",") && s.lastIndexOf(",") > s.lastIndexOf(".")) {
    s = s.replace(/\./g, "").replace(",", ".");
  } else {
    s = s.replace(/,/g, "");
  }
  const n = parseFloat(s);
  return isNaN(n) ? NaN : n;
};

function downloadTemplate() {
  const wb = XLSX.utils.book_new();
  const headers = ["Dalga (B/C/E/BC/EB)", "Kalite", "Birim (m2/kg/ton)", "Fiyat (TL)", "GSM (g/m²)"];
  const sample = [
    ["B", "TST", "m2", "17520,00", "380"],
    ["BC", "TSSST", "m2", "27960,00", "550"],
    ["B", "IKL100 F120 IKL100", "m2", "22680,00", "374"],
    ["BC", "IKL100 F135 F80 F135 IKL100", "m2", "38280,00", "570"],
  ];
  const ws = XLSX.utils.aoa_to_sheet([headers, ...sample]);
  ws["!cols"] = [{ wch: 24 }, { wch: 40 }, { wch: 16 }, { wch: 14 }, { wch: 14 }];
  XLSX.utils.book_append_sheet(wb, ws, "Fiyat Listesi");
  XLSX.writeFile(wb, "karton-fiyat-listesi-sablonu.xlsx");
}

export default function PriceListsManager({ onClose, onChange }) {
  const [lists, setLists] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null); // {items, ...}
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);
  const [pendingItems, setPendingItems] = useState([]); // Excel'den okunan, henüz kaydedilmemiş

  const loadLists = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/pricing/lists");
      setLists(data);
      if (data.length && !selectedId) setSelectedId(data[0].id);
      if (!data.length) setSelectedId(null);
    } finally { setLoading(false); }
  }, [selectedId]);

  const loadDetail = useCallback(async (lid) => {
    if (!lid) { setDetail(null); return; }
    const { data } = await api.get(`/pricing/lists/${lid}`);
    setDetail(data);
  }, []);

  useEffect(() => { loadLists(); }, [loadLists]);
  useEffect(() => { if (selectedId && !creating) loadDetail(selectedId); }, [selectedId, creating, loadDetail]);

  const handleFile = useCallback((file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const wb = XLSX.read(new Uint8Array(ev.target.result), { type: "array", codepage: 65001, raw: false });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const arr = XLSX.utils.sheet_to_json(ws, { header: 1, blankrows: false, defval: "", raw: false });
        if (!arr.length) { toast.error("Dosya boş"); return; }
        let start = 0;
        const first = (arr[0] || []).map((c) => String(c || "").toLowerCase());
        if (first.some((c) => c.includes("dalga") || c.includes("kalite") || c.includes("fiyat"))) start = 1;
        const parsed = [];
        for (let i = start; i < arr.length; i++) {
          const r = arr[i];
          const flute = String(r[0] || "").trim().toUpperCase();
          const quality = String(r[1] || "").trim();
          const unitRaw = String(r[2] || "m2").trim().toLowerCase();
          const unit = UNITS.includes(unitRaw) ? unitRaw : "m2";
          const price = parseTRAmount(r[3]);
          const gsmRaw = r[4];
          const gsm = (gsmRaw === "" || gsmRaw === null || gsmRaw === undefined) ? null : parseTRAmount(gsmRaw);
          if (!flute && !quality && (!price || isNaN(price))) continue;
          parsed.push({
            flute, quality, unit,
            price: isNaN(price) ? null : price,
            gsm: (gsm === null || isNaN(gsm)) ? null : gsm,
            _error:
              !flute ? "Dalga boş"
              : !quality ? "Kalite boş"
              : isNaN(price) || price <= 0 ? "Fiyat geçersiz"
              : "",
          });
        }
        if (!parsed.length) { toast.error("Aktarılacak satır yok"); return; }
        setPendingItems(parsed);
        toast.success(`${parsed.length} satır okundu — kaydetmeden önce inceleyin`);
      } catch (e) { toast.error("Dosya okunamadı: " + (e?.message || "")); }
    };
    reader.readAsArrayBuffer(file);
  }, []);

  const saveNewList = async (form) => {
    const valid = pendingItems.filter((x) => !x._error);
    if (!form.supplier_name || !form.valid_from) {
      toast.error("Tedarikçi adı ve geçerlilik tarihi gerekli"); return;
    }
    try {
      const { data } = await api.post("/pricing/lists", { ...form, items: valid });
      toast.success(`Liste oluşturuldu · ${data.inserted_items} kalem eklendi`);
      setCreating(false); setPendingItems([]);
      await loadLists(); setSelectedId(data.list.id);
      onChange?.();
    } catch (e) { toast.error("Kayıt hatası: " + (e?.response?.data?.detail || e?.message)); }
  };

  const toggleDefault = async (lst) => {
    await api.put(`/pricing/lists/${lst.id}`, { is_default: !lst.is_default });
    toast.success(lst.is_default ? "Varsayılan kaldırıldı" : "Varsayılan yapıldı");
    await loadLists();
    onChange?.();
  };
  const toggleActive = async (lst) => {
    const isActiveNow = !(lst.is_active === false);
    // Aktif → Pasife alınırken varsayılan liste için onay iste
    if (isActiveNow && lst.is_default) {
      const ok = window.confirm(
        `"${lst.supplier_name}" listesi VARSAYILAN olarak işaretli.\n\n` +
        "Pasife alınırsa varsayılan bayrağı otomatik kaldırılacak ve " +
        "Fiyat Hesapla ekranı otomatik olarak diğer aktif listeye geçecek.\n\n" +
        "Devam edilsin mi?"
      );
      if (!ok) return;
      await api.put(`/pricing/lists/${lst.id}`, { is_active: false, is_default: false });
      toast.success("Pasife alındı · varsayılan bayrağı kaldırıldı");
    } else if (isActiveNow) {
      // Normal aktif → pasif
      await api.put(`/pricing/lists/${lst.id}`, { is_active: false });
      toast.success("Pasife alındı");
    } else {
      // Pasif → aktif
      await api.put(`/pricing/lists/${lst.id}`, { is_active: true });
      toast.success("Aktif edildi");
    }
    await loadLists();
    onChange?.();
  };
  const exportListToExcel = async (lst) => {
    try {
      const { data } = await api.get(`/pricing/lists/${lst.id}`);
      const items = data.items || [];
      const wb = XLSX.utils.book_new();
      const headers = ["Dalga (B/C/E/BC/EB)", "Kalite", "Birim (m2/kg/ton)", "Fiyat (TL)", "GSM (g/m²)"];
      const rows = items.map((i) => [
        i.flute || "", i.quality || "", i.unit || "m2",
        i.price != null ? Number(i.price) : "",
        i.gsm != null ? Number(i.gsm) : "",
      ]);
      const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      ws["!cols"] = [{ wch: 24 }, { wch: 40 }, { wch: 16 }, { wch: 14 }, { wch: 14 }];
      XLSX.utils.book_append_sheet(wb, ws, "Fiyat Listesi");
      const safe = `${lst.supplier_name || "liste"}-${lst.valid_from || ""}`.replace(/[^\w\-.]+/g, "_");
      XLSX.writeFile(wb, `${safe}.xlsx`);
      toast.success(`${items.length} satır indirildi`);
    } catch (e) {
      toast.error("Excel oluşturulamadı: " + (e?.response?.data?.detail || e?.message));
    }
  };
  const removeList = async (lst) => {
    if (!window.confirm(`"${lst.supplier_name}" listesi ve tüm kalemleri silinsin mi?`)) return;
    await api.delete(`/pricing/lists/${lst.id}`);
    toast.success("Silindi");
    if (selectedId === lst.id) setSelectedId(null);
    await loadLists();
    onChange?.();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="price-lists-modal">
      <div className="brutal-card bg-white w-full max-w-6xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between border-b-2 border-black px-5 py-3">
          <h2 className="font-display text-xl font-bold">Karton Fiyat Listeleri</h2>
          <button onClick={onClose} className="p-1 hover:bg-black/5"><X size={18} /></button>
        </div>

        <div className="flex-1 flex overflow-hidden">
          {/* Sol: liste */}
          <aside className="w-[260px] border-r border-black/10 flex flex-col">
            <div className="p-3 border-b border-black/10">
              <button
                onClick={() => { setCreating(true); setPendingItems([]); }}
                data-testid="price-list-new"
                className="w-full bg-black text-white text-xs uppercase tracking-widest py-2 flex items-center justify-center gap-1"
              ><Plus size={13} /> Yeni Liste</button>
            </div>
            <div className="flex-1 overflow-auto">
              {loading && <div className="p-4 text-xs text-neutral-500">Yükleniyor…</div>}
              {!loading && lists.length === 0 && (
                <div className="p-4 text-xs text-neutral-500">Henüz liste yok. "Yeni Liste" ile başlayın.</div>
              )}
              <SupplierGroupedLists
                lists={lists}
                selectedId={selectedId}
                creating={creating}
                onSelect={(id) => { setCreating(false); setSelectedId(id); }}
              />
            </div>
          </aside>

          {/* Sağ: detay veya yeni-liste formu */}
          <section className="flex-1 overflow-auto p-5">
            {creating ? (
              <CreateForm
                onCancel={() => { setCreating(false); setPendingItems([]); }}
                onSave={saveNewList}
                pendingItems={pendingItems}
                setPendingItems={setPendingItems}
                onFile={handleFile}
                fileRef={fileRef}
              />
            ) : detail ? (
              <ListDetail
                key={detail.id}
                detail={detail}
                onReload={() => { loadDetail(detail.id); loadLists(); onChange?.(); }}
                onDelete={() => removeList(detail)}
                onToggleDefault={() => toggleDefault(detail)}
                onToggleActive={() => toggleActive(detail)}
                onExport={() => exportListToExcel(detail)}
              />
            ) : (
              <div className="text-sm text-neutral-500">Sol listeden bir kayıt seçin veya yeni liste oluşturun.</div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

function SupplierGroupedLists({ lists, selectedId, creating, onSelect }) {
  // İlk açılışta tüm tedarikçi grupları açık; kullanıcı toggle edebilir
  const [openSuppliers, setOpenSuppliers] = useState(() => new Set());
  const [showInactive, setShowInactive] = useState({});  // { [supplier]: bool }

  const grouped = useMemo(() => {
    const map = {};
    for (const l of lists) {
      const key = l.supplier_name || "(Tedarikçi yok)";
      if (!map[key]) map[key] = { active: [], inactive: [] };
      if (l.is_active === false) map[key].inactive.push(l);
      else map[key].active.push(l);
    }
    return Object.entries(map).sort((a, b) => a[0].localeCompare(b[0], "tr"));
  }, [lists]);

  // İlk render'da tüm grupları default olarak aç
  useEffect(() => {
    if (openSuppliers.size === 0 && grouped.length > 0) {
      setOpenSuppliers(new Set(grouped.map(([k]) => k)));
    }
  }, [grouped, openSuppliers.size]);

  const toggleSupplier = (name) => {
    setOpenSuppliers((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  return (
    <div>
      {grouped.map(([supplier, { active, inactive }]) => {
        const isOpen = openSuppliers.has(supplier);
        const totalCount = active.length + inactive.length;
        return (
          <div key={supplier} className="border-b border-black/10">
            <button
              type="button"
              onClick={() => toggleSupplier(supplier)}
              data-testid={`supplier-group-${supplier}`}
              className="w-full flex items-center gap-2 px-3 py-2 bg-neutral-100 hover:bg-neutral-200"
            >
              {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              <span className="flex-1 text-left text-xs font-bold uppercase tracking-widest truncate">{supplier}</span>
              <span className="text-[10px] text-neutral-600 font-mono-num">{totalCount}</span>
            </button>
            {isOpen && (
              <div>
                {active.map((l) => (
                  <ListRow key={l.id} lst={l} selectedId={selectedId} creating={creating} onSelect={onSelect} />
                ))}
                {inactive.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowInactive((s) => ({ ...s, [supplier]: !s[supplier] }))}
                    data-testid={`toggle-inactive-${supplier}`}
                    className="w-full flex items-center gap-1 px-4 py-1.5 text-[10px] uppercase tracking-widest text-neutral-500 hover:bg-neutral-50"
                  >
                    {showInactive[supplier] ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
                    Pasif Listeler ({inactive.length})
                  </button>
                )}
                {inactive.length > 0 && showInactive[supplier] && (
                  <div className="bg-neutral-50/60">
                    {inactive.map((l) => (
                      <ListRow key={l.id} lst={l} selectedId={selectedId} creating={creating} onSelect={onSelect} dim />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ListRow({ lst, selectedId, creating, onSelect, dim }) {
  const isSelected = selectedId === lst.id && !creating;
  return (
    <div
      onClick={() => onSelect(lst.id)}
      data-testid={`price-list-row-${lst.id}`}
      className={`border-b border-black/5 px-3 py-2.5 pl-6 cursor-pointer ${
        isSelected ? "bg-amber-50" : "hover:bg-neutral-50"
      } ${dim ? "opacity-60" : ""}`}
    >
      <div className="flex items-center justify-between gap-1 mb-0.5">
        <div className="text-sm truncate flex-1">
          {lst.valid_from}{lst.valid_to ? ` — ${lst.valid_to}` : ""}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {lst.is_default && (
            <span className="text-[9px] uppercase tracking-widest bg-emerald-600 text-white px-1.5 py-0.5">Varsayılan</span>
          )}
          {lst.is_active === false && (
            <span className="text-[9px] uppercase tracking-widest bg-neutral-500 text-white px-1.5 py-0.5">Pasif</span>
          )}
        </div>
      </div>
      <div className="text-[10px] text-neutral-500">
        {lst.item_count || 0} kalem
        {(lst.discount_percent || 0) > 0 && (
          <span className="ml-1 text-emerald-700 font-semibold">
            · −%{Number(lst.discount_percent).toLocaleString("tr-TR", { maximumFractionDigits: 2 })}
          </span>
        )}
      </div>
    </div>
  );
}

function CreateForm({ onCancel, onSave, pendingItems, setPendingItems, onFile, fileRef }) {
  const [form, setForm] = useState({
    supplier_name: "",
    valid_from: new Date().toISOString().slice(0, 10),
    valid_to: "",
    is_default: false,
    discount_percent: 0,
    notes: "",
  });
  const valid = pendingItems.filter((x) => !x._error).length;
  const invalid = pendingItems.length - valid;
  const totalPrice = pendingItems.reduce((s, x) => s + (x._error ? 0 : Number(x.price) || 0), 0);

  const updateRow = (i, patch) => {
    setPendingItems((prev) => prev.map((r, idx) => idx === i ? {
      ...r, ...patch,
      _error: (!(patch.flute ?? r.flute)) ? "Dalga boş"
        : (!(patch.quality ?? r.quality)) ? "Kalite boş"
        : (!(patch.price ?? r.price) || (patch.price ?? r.price) <= 0) ? "Fiyat geçersiz"
        : "",
    } : r));
  };
  const addRow = () => setPendingItems((p) => [...p, { flute: "", quality: "", unit: "m2", price: null, gsm: null, _error: "Dalga boş" }]);
  const removeRow = (i) => setPendingItems((p) => p.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
        <div><div className="label-caps mb-1">Tedarikçi *</div>
          <input data-testid="new-supplier-name" className="input" value={form.supplier_name} onChange={(e) => setForm({ ...form, supplier_name: e.target.value })} placeholder="örn. Ideal Ambalaj" /></div>
        <div><div className="label-caps mb-1">Geçerlilik Başlangıç *</div>
          <input data-testid="new-valid-from" type="date" className="input" value={form.valid_from} onChange={(e) => setForm({ ...form, valid_from: e.target.value })} /></div>
        <div><div className="label-caps mb-1">Geçerlilik Bitiş (ops.)</div>
          <input type="date" className="input" value={form.valid_to} onChange={(e) => setForm({ ...form, valid_to: e.target.value })} /></div>
        <div><div className="label-caps mb-1">İskonto (%)</div>
          <input data-testid="new-discount" type="number" step="0.01" min="0" max="100" className="input font-mono-num"
                 value={form.discount_percent} onChange={(e) => setForm({ ...form, discount_percent: parseFloat(e.target.value) || 0 })}
                 placeholder="0" /></div>
        <div className="flex items-end gap-2 pt-4">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" data-testid="new-is-default" checked={form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} /> Varsayılan yap
          </label>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button onClick={() => fileRef.current?.click()} data-testid="import-excel-btn"
                className="bg-black text-white px-3 py-2 text-xs uppercase tracking-widest flex items-center gap-1.5">
          <Upload size={13} /> Excel'den Yükle
        </button>
        <button onClick={downloadTemplate} data-testid="download-template-btn"
                className="border border-black text-black px-3 py-2 text-xs uppercase tracking-widest flex items-center gap-1.5 hover:bg-black hover:text-white">
          <Download size={13} /> Şablon İndir
        </button>
        <button onClick={addRow} className="border border-black/30 px-3 py-2 text-xs uppercase tracking-widest hover:bg-neutral-50 flex items-center gap-1.5">
          <Plus size={13} /> Boş Satır
        </button>
        <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden"
               onChange={(e) => onFile(e.target.files?.[0])} data-testid="new-file-input" />
        {pendingItems.length > 0 && (
          <div className="ml-auto text-xs">
            <span className="text-neutral-500">Toplam:</span> <span className="font-semibold">{pendingItems.length}</span>
            <span className="mx-1 text-neutral-300">|</span>
            <span className="text-neutral-500">Geçerli:</span> <span className="font-semibold text-emerald-700">{valid}</span>
            {invalid > 0 && <><span className="mx-1 text-neutral-300">|</span><span className="text-neutral-500">Hatalı:</span> <span className="font-semibold text-rose-600">{invalid}</span></>}
          </div>
        )}
      </div>

      {pendingItems.length > 0 && (
        <div className="border border-black/10 overflow-auto max-h-[380px]">
          <table className="w-full text-xs">
            <thead className="bg-neutral-50 sticky top-0">
              <tr className="border-b-2 border-black/60">
                <th className="text-left px-2 py-2 label-caps w-24">Dalga</th>
                <th className="text-left px-2 py-2 label-caps">Kalite</th>
                <th className="text-left px-2 py-2 label-caps w-24">Birim</th>
                <th className="text-right px-2 py-2 label-caps w-32">Fiyat</th>
                <th className="text-right px-2 py-2 label-caps w-24">GSM</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {pendingItems.map((r, i) => (
                <tr key={i} className={`border-b border-black/5 ${r._error ? "bg-rose-50" : ""}`}>
                  <td className="px-1 py-1"><input className="input py-1 text-xs" value={r.flute} onChange={(e) => updateRow(i, { flute: e.target.value.toUpperCase() })} /></td>
                  <td className="px-1 py-1"><input className="input py-1 text-xs" value={r.quality} onChange={(e) => updateRow(i, { quality: e.target.value })} /></td>
                  <td className="px-1 py-1">
                    <select className="input py-1 text-xs bg-white" value={r.unit} onChange={(e) => updateRow(i, { unit: e.target.value })}>
                      {UNITS.map((u) => <option key={u}>{u}</option>)}
                    </select>
                  </td>
                  <td className="px-1 py-1"><input className="input py-1 text-xs font-mono-num text-right" type="number" step="0.01" value={r.price ?? ""} onChange={(e) => updateRow(i, { price: parseFloat(e.target.value) || 0 })} /></td>
                  <td className="px-1 py-1"><input className="input py-1 text-xs font-mono-num text-right" type="number" step="0.1" value={r.gsm ?? ""} onChange={(e) => setPendingItems((prev) => prev.map((row, idx) => idx === i ? { ...row, gsm: e.target.value === "" ? null : parseFloat(e.target.value) } : row))} placeholder="—" /></td>
                  <td className="px-1 py-1 text-center"><button onClick={() => removeRow(i)} className="text-rose-600 p-1"><Trash2 size={13} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex justify-end gap-2 pt-3">
        <button onClick={onCancel} className="border border-black/30 px-4 py-2 text-xs uppercase tracking-widest">İptal</button>
        <button onClick={() => onSave(form)} disabled={!valid} data-testid="save-list-btn"
                className="bg-black text-white px-4 py-2 text-xs uppercase tracking-widest disabled:opacity-40 flex items-center gap-1.5">
          <Save size={13} /> {valid} Kalemi Kaydet
        </button>
      </div>

      <style>{`.input{width:100%;border:1px solid rgba(0,0,0,0.2);padding:8px 10px;font-size:13px;outline:none;background:white}.input:focus{border-color:black}`}</style>
    </div>
  );
}

function ListDetail({ detail, onReload, onDelete, onToggleDefault, onToggleActive, onExport }) {
  const [editingId, setEditingId] = useState(null);
  const [rowForm, setRowForm] = useState({ flute: "", quality: "", unit: "m2", price: "", gsm: "" });
  const [adding, setAdding] = useState(false);
  const [addForm, setAddForm] = useState({ flute: "", quality: "", unit: "m2", price: "", gsm: "" });
  const [discountDraft, setDiscountDraft] = useState(String(detail.discount_percent ?? 0));
  const [history, setHistory] = useState({});

  useEffect(() => { setDiscountDraft(String(detail.discount_percent ?? 0)); }, [detail.id, detail.discount_percent]);

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const { data } = await api.get(`/pricing/lists/${detail.id}/price-history`);
        if (!ignore) setHistory(data.history || {});
      } catch { if (!ignore) setHistory({}); }
    })();
    return () => { ignore = true; };
  }, [detail.id]);

  const gsmKey = (g) => {
    if (g === null || g === undefined || g === "") return "";
    const f = Number(g);
    if (isNaN(f)) return String(g);
    return Number.isInteger(f) ? String(f) : String(f);
  };
  const seriesForItem = (it) => history[`${it.flute}|${it.quality}|${gsmKey(it.gsm)}`] || [];

  const saveDiscount = async () => {
    const val = Math.max(0, Math.min(100, parseFloat(discountDraft) || 0));
    try {
      await api.put(`/pricing/lists/${detail.id}`, { discount_percent: val });
      toast.success(`İskonto %${val} olarak kaydedildi`);
      onReload();
    } catch { toast.error("İskonto kaydedilemedi"); }
  };

  const startEdit = (i) => { setEditingId(i.id); setRowForm({ flute: i.flute, quality: i.quality, unit: i.unit, price: i.price, gsm: i.gsm ?? "" }); };
  const saveEdit = async () => {
    try {
      await api.put(`/pricing/lists/items/${editingId}`, {
        ...rowForm,
        price: parseFloat(rowForm.price),
        gsm: rowForm.gsm === "" || rowForm.gsm === null ? null : parseFloat(rowForm.gsm),
      });
      setEditingId(null); onReload(); toast.success("Güncellendi");
    } catch (e) { toast.error("Güncellenemedi"); }
  };
  const delItem = async (iid) => {
    if (!window.confirm("Kalem silinsin mi?")) return;
    await api.delete(`/pricing/lists/items/${iid}`); onReload(); toast.success("Silindi");
  };
  const addItem = async () => {
    if (!addForm.flute || !addForm.quality || !addForm.price) { toast.error("Dalga/Kalite/Fiyat gerekli"); return; }
    await api.post(`/pricing/lists/${detail.id}/items`, { items: [{
      ...addForm,
      price: parseFloat(addForm.price),
      gsm: addForm.gsm === "" ? null : parseFloat(addForm.gsm),
    }] });
    setAdding(false); setAddForm({ flute: "", quality: "", unit: "m2", price: "", gsm: "" });
    onReload(); toast.success("Eklendi");
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="font-display text-2xl font-bold">{detail.supplier_name}</h3>
          <p className="text-xs text-neutral-500">
            {detail.valid_from}{detail.valid_to ? ` — ${detail.valid_to}` : ""} · {(detail.items || []).length} kalem
            {(detail.discount_percent || 0) > 0 && (
              <span className="ml-2 text-emerald-700 font-semibold">· İskonto %{Number(detail.discount_percent).toLocaleString("tr-TR", { maximumFractionDigits: 2 })}</span>
            )}
            {detail.is_active === false && (
              <span className="ml-2 text-neutral-500 uppercase tracking-widest text-[10px] bg-neutral-200 px-1.5 py-0.5 border border-neutral-400">Pasif</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1 border border-black/20 px-2 py-1">
            <span className="text-[10px] uppercase tracking-widest text-neutral-500 mr-1">İskonto %</span>
            <input
              type="number" step="0.01" min="0" max="100"
              value={discountDraft}
              onChange={(e) => setDiscountDraft(e.target.value)}
              className="w-16 border-0 py-0.5 px-1 text-xs font-mono-num text-right focus:outline-none"
              data-testid="edit-discount"
            />
            <button onClick={saveDiscount} data-testid="save-discount" className="text-emerald-700 p-1"><Save size={13} /></button>
          </div>
          <button onClick={onExport} data-testid="export-list-xlsx"
                  className="border border-black px-3 py-1.5 text-xs uppercase tracking-widest hover:bg-black hover:text-white flex items-center gap-1.5">
            <FileDown size={13} /> Excel İndir
          </button>
          <button onClick={onToggleDefault}
                  className="border border-black px-3 py-1.5 text-xs uppercase tracking-widest hover:bg-black hover:text-white flex items-center gap-1.5">
            {detail.is_default ? <><StarOff size={13} /> Varsayılan Kaldır</> : <><Star size={13} /> Varsayılan Yap</>}
          </button>
          <button onClick={onToggleActive} data-testid="toggle-active"
                  className={`border px-3 py-1.5 text-xs uppercase tracking-widest flex items-center gap-1.5 ${
                    detail.is_active === false
                      ? "border-emerald-600 text-emerald-700 hover:bg-emerald-600 hover:text-white"
                      : "border-neutral-600 text-neutral-700 hover:bg-neutral-800 hover:text-white"
                  }`}>
            {detail.is_active === false ? <><Power size={13} /> Aktif Et</> : <><PowerOff size={13} /> Pasife Al</>}
          </button>
          <button onClick={onDelete} className="border border-rose-500 text-rose-600 px-3 py-1.5 text-xs uppercase tracking-widest hover:bg-rose-600 hover:text-white flex items-center gap-1.5">
            <Trash2 size={13} /> Listeyi Sil
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button onClick={() => setAdding((v) => !v)} className="border border-black/30 px-3 py-1.5 text-xs uppercase tracking-widest hover:bg-neutral-50 flex items-center gap-1.5">
          <Plus size={13} /> Kalem Ekle
        </button>
      </div>

      {adding && (
        <div className="border border-black/20 p-3 bg-amber-50 grid grid-cols-6 gap-2 items-end">
          <div><div className="label-caps mb-1">Dalga</div><input className="input" value={addForm.flute} onChange={(e) => setAddForm({ ...addForm, flute: e.target.value.toUpperCase() })} /></div>
          <div className="col-span-2"><div className="label-caps mb-1">Kalite</div><input className="input" value={addForm.quality} onChange={(e) => setAddForm({ ...addForm, quality: e.target.value })} /></div>
          <div><div className="label-caps mb-1">Birim</div>
            <select className="input bg-white" value={addForm.unit} onChange={(e) => setAddForm({ ...addForm, unit: e.target.value })}>
              {UNITS.map((u) => <option key={u}>{u}</option>)}
            </select>
          </div>
          <div><div className="label-caps mb-1">Fiyat</div>
            <input type="number" step="0.01" className="input font-mono-num" value={addForm.price} onChange={(e) => setAddForm({ ...addForm, price: e.target.value })} />
          </div>
          <div><div className="label-caps mb-1">GSM</div>
            <div className="flex gap-1">
              <input type="number" step="0.1" className="input font-mono-num" value={addForm.gsm} onChange={(e) => setAddForm({ ...addForm, gsm: e.target.value })} placeholder="ops." />
              <button onClick={addItem} className="bg-black text-white px-2"><CheckCircle2 size={14} /></button>
            </div>
          </div>
        </div>
      )}

      <div className="border border-black/10 overflow-auto max-h-[460px]">
        <table className="w-full text-xs">
          <thead className="bg-neutral-50 sticky top-0">
            <tr className="border-b-2 border-black/60">
              <th className="text-left px-3 py-2 label-caps w-20">Dalga</th>
              <th className="text-left px-3 py-2 label-caps">Kalite</th>
              <th className="text-left px-3 py-2 label-caps w-20">Birim</th>
              <th className="text-right px-3 py-2 label-caps w-28">Fiyat (TL)</th>
              <th className="text-left px-3 py-2 label-caps w-32">Trend</th>
              <th className="text-right px-3 py-2 label-caps w-24">GSM</th>
              <th className="w-24 px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {(detail.items || []).length === 0 && (
              <tr><td colSpan={7} className="text-center py-8 text-neutral-500">Bu listede henüz kalem yok.</td></tr>
            )}
            {(detail.items || []).map((it) => editingId === it.id ? (
              <tr key={it.id} className="bg-amber-50 border-b border-black/5">
                <td className="px-2 py-1"><input className="input py-1 text-xs" value={rowForm.flute} onChange={(e) => setRowForm({ ...rowForm, flute: e.target.value.toUpperCase() })} /></td>
                <td className="px-2 py-1"><input className="input py-1 text-xs" value={rowForm.quality} onChange={(e) => setRowForm({ ...rowForm, quality: e.target.value })} /></td>
                <td className="px-2 py-1">
                  <select className="input py-1 text-xs bg-white" value={rowForm.unit} onChange={(e) => setRowForm({ ...rowForm, unit: e.target.value })}>
                    {UNITS.map((u) => <option key={u}>{u}</option>)}
                  </select>
                </td>
                <td className="px-2 py-1"><input type="number" step="0.01" className="input py-1 text-xs font-mono-num text-right" value={rowForm.price} onChange={(e) => setRowForm({ ...rowForm, price: e.target.value })} /></td>
                <td className="px-2 py-1 text-neutral-300 text-[10px]">—</td>
                <td className="px-2 py-1"><input type="number" step="0.1" className="input py-1 text-xs font-mono-num text-right" value={rowForm.gsm} onChange={(e) => setRowForm({ ...rowForm, gsm: e.target.value })} /></td>
                <td className="px-2 py-1 text-right"><button onClick={saveEdit} className="text-emerald-700 mr-2"><Save size={14} /></button><button onClick={() => setEditingId(null)}><X size={14} /></button></td>
              </tr>
            ) : (
              <tr key={it.id} className="border-b border-black/5 hover:bg-neutral-50">
                <td className="px-3 py-2 font-semibold">{it.flute}</td>
                <td className="px-3 py-2">{it.quality}</td>
                <td className="px-3 py-2">{it.unit}</td>
                <td className="px-3 py-2 text-right font-mono-num font-semibold">
                  {Number(it.price).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 4 })}
                </td>
                <td className="px-3 py-2" data-testid={`trend-${it.id}`}>
                  <PriceSparkline series={seriesForItem(it)} currentPrice={it.price} />
                </td>
                <td className="px-3 py-2 text-right font-mono-num">
                  {it.gsm ? Number(it.gsm).toLocaleString("tr-TR", { maximumFractionDigits: 1 }) : <span className="text-neutral-400">—</span>}
                </td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => startEdit(it)} className="text-neutral-600 mr-2"><Edit3 size={13} /></button>
                  <button onClick={() => delItem(it.id)} className="text-rose-600"><Trash2 size={13} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <style>{`.input{width:100%;border:1px solid rgba(0,0,0,0.2);padding:8px 10px;font-size:13px;outline:none;background:white}.input:focus{border-color:black}`}</style>
    </div>
  );
}
